import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openWebCenterDecisionCall } from '@/lib/analytics';
import {
  ArrowRight,
  Calendar,
  Search,
  FileSearch,
  Users,
  BookOpen,
  ScanLine,
  BrainCircuit,
  Wrench,
  Library,
  Workflow,
} from 'lucide-react';
import Navigation from '@/components/Navigation';
import SEO from '@/components/SEO';
import Breadcrumbs from '@/components/Breadcrumbs';
import WebCenterEngagementHistory from '@/components/WebCenterEngagementHistory';
import LastUpdated from '@/components/LastUpdated';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FOUNDER, founderPersonLd } from '@/lib/founder';

const KEY_FACTS = [
  {
    icon: Library,
    title: 'Designer + Runtime + Verifier',
    detail:
      'WFR ships as three coordinated tools: the Designer (rule authoring environment), the Runtime engine (executes extraction against incoming documents), and the Verifier (operator interface for low-confidence results). Each has its own configuration surface and its own learning curve.',
  },
  {
    icon: ScanLine,
    title: 'Per-supplier template libraries',
    detail:
      'WFR projects accumulate supplier-specific templates over years — header rules, line-item rules, tax-rule logic, currency handling, and locale variations. The template library is the actual asset; the runtime is just the engine that executes it.',
  },
  {
    icon: BrainCircuit,
    title: 'Example-based recognition + neural validation',
    detail:
      'WFR combines rule-based extraction authored in the Designer with neural-network validation that learns from corrected examples. The learnt-data files accumulated over years of Verifier sessions are part of the system\'s production accuracy — they are not regenerable from scratch.',
  },
  {
    icon: Workflow,
    title: 'Integration with WebCenter Imaging and WCC',
    detail:
      'WFR delivers extracted data into WebCenter Imaging (IPM) or WebCenter Content (UCM) via the integration adapter, which then routes invoices into the AP workflow. The handoff schema, metadata mappings, and exception-routing rules are project-specific.',
  },
];

const SCENARIOS = [
  {
    icon: Users,
    title: 'Original implementers have moved on',
    body:
      'The team that authored the WFR project — Designer rule sets, Verifier workflows, integration adapter configuration — has changed roles, left, or retired. Documentation is partial or missing. The system runs in production, but no one inside the customer organization can confidently modify it.',
  },
  {
    icon: Library,
    title: 'Per-supplier templates outnumber AP team capacity',
    body:
      'New suppliers arrive faster than the team can author and tune templates. The backlog of supplier-specific extraction rules grows. AP keyers absorb the overflow as manual entry, and the original ROI case for WFR quietly erodes.',
  },
  {
    icon: Wrench,
    title: 'WFR rules need updates, consulting capacity is thin',
    body:
      'A tax-jurisdiction change, a supplier ERP upgrade, or a layout refresh broke a high-volume template. The customer has tried to source someone who can read the existing Designer project and make a clean change. Calendars have been quiet.',
  },
  {
    icon: BrainCircuit,
    title: 'Considering AI extraction, no one can validate parity',
    body:
      'The customer is evaluating modern AI extraction as a replacement for WFR. The blocker is not the new platform — it is that nobody on the team can read the existing WFR rule library to confirm the new system covers every supplier and every edge case the WFR project already handles.',
  },
];

const HOW_WE_READ = [
  {
    step: '1',
    title: 'Designer file analysis',
    body:
      'Open the WFR Designer project directly, inspect the document classification rules, header-region templates, line-item extraction rules, and validation logic for each supplier in the library. Inventory every active rule, every disabled rule, and every edge-case branch.',
  },
  {
    step: '2',
    title: 'Project export inspection',
    body:
      'Export the WFR project to its serialized form and inspect the underlying definitions for rules that are not visible in the Designer UI — auto-generated artifacts, deprecated rule formats from older WFR releases, and rule-naming conventions that signal historical implementation patterns.',
  },
  {
    step: '3',
    title: 'Rule library reverse-engineering',
    body:
      'For each high-volume supplier, document what the rules actually do — which fields they extract, how the line-item logic handles multi-line headers, how tax fields are resolved, how the rule falls through to the Verifier when confidence is below threshold. Output is a plain-language rule book your team can read.',
  },
  {
    step: '4',
    title: 'Supplier coverage matrix',
    body:
      'Cross-reference the rule library against the actual supplier population from the last 12-24 months of AP transactions. Surface which suppliers have dedicated templates, which fall through to generic templates, and which generate the bulk of Verifier touches.',
  },
  {
    step: '5',
    title: 'Learnt-data inspection',
    body:
      'Inspect the neural-validation learnt-data files accumulated from Verifier sessions over the system\'s lifetime. Quantify what the learnt data is contributing to live extraction accuracy — relevant for any parity discussion against an AI replacement that would start from a cold model.',
  },
];

const ENGAGEMENT_TIERS = [
  {
    tag: 'Read',
    title: 'WFR template audit',
    body:
      'A fixed-fee read of the existing WFR project. Output is the plain-language rule book, supplier coverage matrix, and learnt-data assessment. The audit is yours to keep regardless of whether the next phase is WFR modernization, migration to 14c, or replacement with AI extraction.',
  },
  {
    tag: 'Modernize',
    title: 'WFR rule migration to 14c',
    body:
      'Migrate the existing Designer project, template library, integration adapter configuration, and learnt data forward into the WFR 14c runtime. Validate each supplier template against the 14c rule format. Re-establish the Verifier workflow and operator queues.',
  },
  {
    tag: 'Compare',
    title: 'Replacement parity analysis',
    body:
      'For customers weighing a move to AI extraction, document what the existing rule library does and define a parity threshold the new system must meet before cutover. Pairs naturally with a Designer-project read — there is no parity analysis without first reading the rules.',
  },
  {
    tag: 'Tune',
    title: 'Verifier workflow tuning',
    body:
      'Adjust operator queues, confidence thresholds, and exception-routing to bring down the Verifier touch rate. Targeted work on the highest-volume suppliers, where a small rule change removes a large share of manual review.',
  },
];

const FAQS = [
  {
    q: 'What is Oracle WebCenter Forms Recognition (WFR)?',
    a: 'Oracle WebCenter Forms Recognition is the rule-based document extraction product inside the Oracle WebCenter stack. WFR ships as three coordinated tools: the Designer (rule authoring environment), the Runtime engine (executes the extraction against incoming documents), and the Verifier (operator interface for low-confidence results). WFR projects accumulate supplier-specific templates over years — header rules, line-item logic, tax handling, currency, locale variations — and integrate with WebCenter Imaging or WebCenter Content via an integration adapter that delivers extracted data into the AP workflow.',
  },
  {
    q: 'Can you read our existing WFR project?',
    a: 'Yes — reading the existing WFR Designer project is the core of the engagement. Andrew opens the project directly, inspects every document classification rule, header template, line-item rule, and validation logic branch in the library, and produces a plain-language rule book your team can read. The read covers both Designer-visible artifacts and the underlying project export for rules that are not visible in the UI. This work does not require the original implementers to be available, and does not require existing documentation.',
  },
  {
    q: 'Do we have to replace WFR or can we modernize it?',
    a: 'Both are legitimate paths — modernization in place and replacement with AI extraction are each defensible depending on supplier composition. WFR modernization in place — bringing the Designer project, template library, integration adapter, and learnt data forward into WFR 14c — preserves the existing rule investment and the accumulated neural-validation training. Replacement with AI extraction makes sense for long-tail supplier populations and semi-structured formats where rule maintenance has become the bottleneck. A template audit gives you the data to make that decision honestly, based on your supplier composition, the maintenance load on AP, and what the existing rules are actually contributing to live accuracy.',
  },
  {
    q: 'How does WFR work after Oracle Fusion Middleware 12c support ends?',
    a: 'Oracle Fusion Middleware 12c Premier Support ends December 2026 and Extended Support ends December 2027, and the same support lifecycle applies to WFR since it runs as part of the WebCenter Imaging deployment. The forward paths are: upgrade WFR to 14c alongside the rest of the WebCenter stack, migrate to a WFR-on-OCI deployment pattern, or replace WFR with a modern AI extraction layer. See our [WebCenter Decision Guide](/resources/webcenter-decision-guide) for the full framework.',
  },
  {
    q: 'Is WFR being deprecated by Oracle?',
    a: 'No. Oracle is adding AI-augmented extraction patterns alongside the existing WFR engine — including integration points with OCI Document Understanding in the 14c release. WFR remains the rule-based extraction surface within WebCenter; the new surface area sits next to it, not in place of it. What customers are deciding is not whether Oracle is retiring WFR, but how they want to read, evolve, and modernize their existing project going forward.',
  },
  {
    q: 'What does a WFR reading engagement look like?',
    a: 'A WFR reading engagement combines Designer file analysis, project export inspection, rule library reverse-engineering, a supplier coverage matrix, and learnt-data inspection — delivered as a structured rule book and matrix the customer keeps. Andrew does this work directly, so the depth of the read matches the depth of the project. The output is independent of the decision that comes next — whether the customer modernizes WFR, migrates to 14c, or replaces it, the read is the input to that decision.',
  },
  {
    q: 'What is a typical timeline?',
    a: 'A focused WFR template audit on a small-to-mid library (under 100 supplier templates) typically runs 2-4 weeks, and a full read of a complex library (200+ supplier templates, multiple business units, accumulated learnt data) typically runs 6-12 weeks. WFR rule migration to 14c overlaps with the broader 12c-to-14c upgrade timeline (see /solutions/webcenter-14c-upgrade) and is sized against that scope. Replacement parity analysis pairs with the broader AP modernization timeline.',
  },
  {
    q: 'How do WFR engagements typically work?',
    a: 'Most WFR work is scoped as a fixed-fee project with a defined deliverable — a template audit, a rule migration to 14c, a parity analysis, or Verifier tuning. Andrew is personally on the engagement rather than parcelling it to junior staff, because reading a WFR project does not decompose cleanly — every rule depends on context from other rules. For larger efforts, that same hands-on involvement runs alongside your team through the delivery window. Scope and pricing are established per situation on a Decision Call.',
  },
];

const WebCenterFormsRecognitionExperts = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const personSchema = { '@context': 'https://schema.org', ...founderPersonLd };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline:
      'Oracle WebCenter Forms Recognition (WFR) Experts — Template Reading, Modernization & Migration | EZ Cloud',
    description:
      'Deep Oracle WebCenter Forms Recognition (WFR) expertise. EZ Cloud reads your existing WFR Designer project directly — template library, rules, learnt data — and delivers a plain-language rule book, WFR modernization, migration to 14c, or replacement with AI extraction. Founder-led by Andrew Blackman, 25-year Oracle specialist.',
    author: { '@id': 'https://ezcloud.ai/about#andrew-blackman' },
    publisher: { '@id': 'https://ezcloud.ai/#organization' },
    datePublished: '2026-06-29',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://ezcloud.ai/solutions/webcenter-forms-recognition-experts',
    },
    about: [
      { '@type': 'Thing', name: 'Oracle WebCenter Forms Recognition' },
      { '@type': 'Thing', name: 'WFR' },
      { '@type': 'Thing', name: 'Forms Recognition Designer' },
      { '@type': 'Thing', name: 'WFR template library' },
      { '@type': 'Thing', name: 'Oracle WebCenter Content' },
      { '@type': 'Thing', name: 'Oracle WebCenter Imaging' },
      { '@type': 'Thing', name: 'Oracle WebCenter Enterprise Capture' },
      { '@type': 'Thing', name: 'Oracle SOA Suite' },
      { '@type': 'Thing', name: 'Oracle WebLogic Server' },
      { '@type': 'Thing', name: 'Accounts Payable Automation' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Oracle WebCenter Forms Recognition (WFR) Experts — Template Reading, Modernization & Migration | EZ Cloud"
        description="Deep Oracle WebCenter Forms Recognition (WFR) expertise. EZ Cloud reads your existing WFR Designer project directly — template library, rules, learnt data — and delivers a plain-language rule book, WFR modernization, migration to 14c, or replacement with AI extraction. Founder-led by Andrew Blackman, 25-year Oracle specialist."
        canonical="https://ezcloud.ai/solutions/webcenter-forms-recognition-experts"
        breadcrumbs={[
          { name: 'Home', url: 'https://ezcloud.ai/' },
          { name: 'Solutions', url: 'https://ezcloud.ai/solutions/modernize-oracle-webcenter' },
          {
            name: 'WebCenter Forms Recognition Experts',
            url: 'https://ezcloud.ai/solutions/webcenter-forms-recognition-experts',
          },
        ]}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navigation />
      <Breadcrumbs />
      <LastUpdated date="2026-06-29" />

      <main>
        {/* Hero */}
        <section className="bg-hero-gradient text-white pt-16 pb-20 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-br from-primary/20 to-transparent" />
            <div className="absolute top-20 right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="container mx-auto px-4 max-w-5xl relative">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              <FileSearch className="w-4 h-4" aria-hidden />
              Oracle WebCenter Forms Recognition · Founder-led
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Oracle WebCenter Forms Recognition experts
              <span className="block text-accent mt-3 text-2xl md:text-3xl lg:text-4xl font-bold">
                Read directly from your existing Designer project.
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 max-w-3xl mb-8">
              WFR template reading, modernization, and migration to 14c — by {FOUNDER.name}, a
              25-year Oracle specialist with 20+ years inside the WebCenter stack, including Forms
              Recognition Designer, Runtime, and Verifier.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('wfr-experts-hero')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Talk to Andrew about your WFR project
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* TL;DR */}
        <section className="py-14 bg-white border-b border-gray-100">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3">
              What we do
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5 leading-tight">
              Deep, specialized WFR expertise — read directly from your project
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                <strong className="text-gray-900">
                  Oracle WebCenter Forms Recognition (WFR) is the rule-based document extraction
                  product inside the Oracle WebCenter stack.
                </strong>{' '}
                It ships as three coordinated tools — the Designer (rule authoring), the Runtime
                engine (executes extraction), and the Verifier (operator interface for low-confidence
                results) — and integrates with WebCenter Imaging or WebCenter Content to deliver
                extracted invoice data into the AP workflow.
              </p>
              <p>
                WFR projects run supplier-template libraries that accumulated over a decade — header
                rules, line-item logic, tax handling, currency, locale variations, and the learnt
                data behind the neural validation. Reading and evolving that library takes specific,
                hands-on WFR knowledge across the Designer, the Runtime, and the Verifier. That is
                exactly the work EZ Cloud does.
              </p>
              <p>
                We read WFR projects directly — Designer file analysis, project export inspection,
                rule library reverse-engineering, supplier coverage matrix, learnt-data inspection.
                No original implementers required, no prior documentation required. You get back a
                plain-language rule book your team can read and reason about — the input to whatever
                comes next, whether that is modernizing WFR, migrating to 14c, or replacing it with
                AI extraction.
              </p>
            </div>
          </div>
        </section>

        {/* What WFR actually is */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What WFR actually is
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Designer, Runtime, Verifier, and the integration adapter
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              WFR is not a single product surface. It is four interacting parts that together
              produce the AP extraction behaviour customers depend on.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {KEY_FACTS.map(({ icon: Icon, title, detail }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-1">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{detail}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Common scenarios we hear */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common scenarios we hear
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four situations that lead customers to this page
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The pattern is consistent across customers and verticals. The WFR runtime is doing its
              job; the question is who can read and maintain it going forward.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {SCENARIOS.map(({ icon: Icon, title, body }) => (
                <Card key={title} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-accent" aria-hidden />
                    </div>
                    <div>
                      <h3 className="text-gray-900 font-semibold mb-2">{title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How EZ Cloud reads your WFR project */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud reads your WFR project
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Five steps from "no one understands this" to a plain-language rule book
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              The output is independent of the decision that comes next. Whether the next phase is
              WFR modernization, migration to 14c, or replacement with AI extraction, the read is the
              input you cannot skip.
            </p>
            <div className="space-y-5">
              {HOW_WE_READ.map((s) => (
                <Card key={s.step} className="p-6 bg-white border border-gray-200 shadow-sm">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-lg bg-accent/15 flex items-center justify-center text-accent font-bold text-xl flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{s.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{s.body}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/resources/wfr-template-analyzer"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the WFR template analyzer resource
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Engagement options — four concrete deliverables */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              How EZ Cloud engages on WFR work
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              Four ways WFR engagements are scoped
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Each is a fixed-fee project with a defined deliverable, and each starts from reading
              the existing Designer project. Andrew is personally on the work — reading a WFR project
              does not decompose cleanly, because every rule depends on context from other rules.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {ENGAGEMENT_TIERS.map((t) => (
                <Card
                  key={t.title}
                  className="p-7 shadow-sm bg-white border border-gray-200"
                >
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    {t.tag}
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{t.body}</p>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto">
              Scope and pricing are established per situation on a Decision Call.
            </p>
          </div>
        </section>

        {/* What 14c changes for WFR */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              What 14c changes for WFR
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              AI-augmented extraction sits alongside the existing engine
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Oracle is adding AI-augmented extraction patterns alongside the existing WFR engine in
              the 14c release. The rule-based runtime is not being removed — the new surface area is
              additive.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <FileSearch className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-semibold mb-1">REST API surface</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Native REST endpoints across the WebCenter 14c stack — replacing
                      proprietary-protocol-only patterns from earlier releases. WFR result delivery,
                      Verifier interactions, and project administration become reachable through the
                      same API plane.
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <BrainCircuit className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-semibold mb-1">
                      OCI Document Understanding integration
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Integration points with Oracle&apos;s OCI Document Understanding service for
                      AI-augmented extraction patterns. Long-tail suppliers and semi-structured
                      formats — historically the hardest cases for pure rule-based WFR — gain a
                      complementary path inside the same WebCenter envelope.
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Library className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-semibold mb-1">
                      Existing rule library forward-compatible
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      The Designer projects, template libraries, learnt-data files, and integration
                      adapter configurations from 12c migrate forward into 14c with validation work
                      against the 14c rule format. The investment built over years of Verifier
                      sessions does not have to start from scratch.
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-6 bg-white border border-gray-200 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-lg bg-accent/20 flex items-center justify-center flex-shrink-0">
                    <Workflow className="w-5 h-5 text-accent" aria-hidden />
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-semibold mb-1">
                      Verifier workflows preserved
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      The Verifier operator interface, queue configuration, confidence thresholds,
                      and exception-routing logic carry forward. AP teams that have trained on the
                      Verifier do not have to retrain on a different operator surface.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/webcenter-14c-upgrade"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the full 12c to 14c upgrade approach
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* WFR vs AI extraction */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-5xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              WFR vs AI extraction — the real comparison
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              The decision is about your supplier population, not the technology
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
              Both approaches have a legitimate place. The right answer turns on what the supplier
              population looks like and where the maintenance load lands.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Where WFR is the right answer
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  High-confidence rule-based extraction on stable supplier sets
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  WFR shines on stable, high-volume supplier populations where templates have been
                  authored and tuned, learnt data has accumulated, and extraction accuracy is
                  predictable. The rule-based approach gives auditable extraction logic — every
                  field has a traceable rule — which matters in regulated environments.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    Supplier set is concentrated and stable
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    Auditability of extraction logic is a requirement
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    Existing learnt-data investment is meaningful
                  </li>
                </ul>
              </Card>
              <Card className="p-7 bg-white border border-gray-200 shadow-sm">
                <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                  Where AI extraction is the right answer
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Long-tail suppliers, semi-structured formats, continuous learning
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  AI extraction is the better fit when the supplier population is long-tail (every
                  new supplier brings a new layout), when invoices are semi-structured (PDF + email
                  + scanned attachments), and when the customer would otherwise be authoring
                  templates faster than AP can process invoices.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    Long-tail supplier population, high layout variance
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    Template-authoring load has become the bottleneck
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" aria-hidden />
                    Continuous-learning extraction is acceptable to AP and audit
                  </li>
                </ul>
              </Card>
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/modernize-webcenter-forms-recognition"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the WFR modernization path in detail
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* The value of a readable rule book */}
        <section className="py-12 bg-white">
          <div className="container mx-auto px-4 max-w-4xl">
            <Card className="p-8 bg-accent/5 border border-accent/30 shadow-sm">
              <div className="flex items-start gap-4">
                <BookOpen
                  className="w-6 h-6 text-accent flex-shrink-0 mt-1"
                  aria-hidden
                />
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
                    What you get back
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    A Designer project turned into a rule book your team can read
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-3">
                    The WFR runtime is supported and the integration adapter delivers invoices into
                    AP on the same path it always has. What is often missing is a readable account of
                    what the project actually does — which rules fire for which suppliers, how
                    line-item logic resolves, and what the accumulated learnt data contributes to
                    live accuracy.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Reading the project converts it from Designer artifacts into a plain-language rule
                    book and supplier coverage matrix. Whatever comes next — WFR modernization,
                    migration to 14c, or replacement with AI extraction — starts from understanding
                    rather than guesswork.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </section>

        {/* Beyond WFR — the full-stack advantage */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Beyond WFR
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-4">
              WFR work rarely stays inside WFR — and here it doesn&apos;t need a second vendor
            </h2>
            <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
              A WFR engagement almost always touches the technologies around it. This is not a
              WFR-only boutique — it is the same founder, hands-on, across the whole stack.
            </p>
            <div className="space-y-4 text-gray-700 leading-relaxed max-w-3xl mx-auto">
              <p>
                WFR delivers into{' '}
                <Link to="/solutions/modernize-webcenter-imaging" className="text-primary hover:text-accent font-medium transition-colors">
                  WebCenter Imaging
                </Link>{' '}
                (the invoice repository), receives documents from{' '}
                <Link to="/solutions/modernize-webcenter-enterprise-capture" className="text-primary hover:text-accent font-medium transition-colors">
                  WebCenter Enterprise Capture
                </Link>{' '}
                (the scan and email front door), routes through{' '}
                <Link to="/solutions/oracle-soa-suite-migration" className="text-primary hover:text-accent font-medium transition-colors">
                  SOA Suite composites
                </Link>
                , runs on{' '}
                <Link to="/solutions/oracle-weblogic-14c-upgrade" className="text-primary hover:text-accent font-medium transition-colors">
                  WebLogic Server
                </Link>
                , and integrates with Oracle E-Business Suite or Fusion Cloud. The same 25-year
                founder who reads your WFR Designer project works directly across all of it —
                Content (UCM), Imaging (IPM), Enterprise Capture, SOA, WebLogic, ADF, BPM, FIPSA,
                and Managed Attachments for EBS — across WebCenter 11g, 12c, and 14c.
              </p>
              <p>
                So a WFR project that expands into the repository, the capture layer, the workflow,
                or the ERP integration does not require a second vendor or a delivery team handed a
                runbook. The three questions worth asking any WFR partner — who specifically is
                assigned, how many WFR projects they have delivered, and whether they have experience
                with your exact version (11g, 12c, or 14c) — have the same answer here every time:
                the founder, personally, across all three.
              </p>
            </div>
            <div className="text-center mt-8">
              <Link
                to="/solutions/modernize-oracle-webcenter"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors"
              >
                See the full WebCenter modernization capability
                <ArrowRight className="w-4 h-4 ml-1" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        {/* Cross-link to decision guide */}
        <section className="py-12 bg-accent/5 border-y border-accent/30">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <p className="text-xs font-bold uppercase tracking-wide text-accent mb-2">
              Free resource
            </p>
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
              The Oracle WebCenter AP December 2026 Decision Guide
            </h3>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              The structured guide to the four forward paths from the 12c support cliff, with WFR
              treated as its own decision inside the broader WebCenter framework. Eight diagnostic
              questions and a vendor evaluation checklist.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/resources/webcenter-decision-guide"
                className="inline-flex items-center font-semibold text-white bg-accent hover:bg-accent/90 transition-colors px-6 py-3 rounded-lg"
              >
                Read the Decision Guide
                <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
              </Link>
              <Link
                to="/solutions/webcenter-12c-end-of-support-decision"
                className="inline-flex items-center font-semibold text-primary hover:text-accent transition-colors px-6 py-3"
              >
                See the 12c end-of-support decision
                <ArrowRight className="w-4 h-4 ml-2" aria-hidden />
              </Link>
            </div>
          </div>
        </section>

        <WebCenterEngagementHistory />

        {/* FAQ */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-accent mb-3 text-center">
              Common questions
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-10">
              Direct answers on WFR engagements
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`item-${i}`}>
                  <AccordionTrigger className="text-left text-gray-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-gray-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-hero-gradient text-white">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Talk to Andrew about your WFR project
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              A 30-minute Decision Call with {FOUNDER.name}, founder of EZ Cloud and a 25-year
              Oracle specialist with 20+ years inside the WebCenter stack — Forms Recognition
              Designer, Runtime, and Verifier; Content (UCM); Imaging (IPM); Capture (ODC); SOA
              Suite; WebLogic; ADF. Walk through your WFR project, the supplier library, and the
              forward options in the context of your specific implementation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="lg"
                variant="accent"
                className="font-semibold"
                onClick={() => openWebCenterDecisionCall('wfr-experts-cta')}
              >
                <Calendar className="mr-2 w-5 h-5" aria-hidden />
                Talk to Andrew about your WFR project
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden />
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <Link to="/solutions/modernize-oracle-webcenter">
                  See the full modernization path
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default WebCenterFormsRecognitionExperts;
